import React from 'react';

import { Box, Chip, Tooltip } from '@mui/material';

import { CommissionsConditionTypes, ruleConditionsToTextMap } from '~/consts/agentHub';

import type { CommissionFlightCarrierRule } from '~/types/services/agentHub';

type CommissionRuleValue = number | CommissionFlightCarrierRule | { airlineCode?: string; airlineName?: string };

interface Props {
  rules: Record<string, Array<CommissionRuleValue>>;
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}

interface MappedRules {
  agents?: Record<string, string>;
  agencies?: Record<string, string>;
}

function commissionRuleTypeLabel(key: string): string {
  if (key === 'carrier') {
    return ruleConditionsToTextMap.get('flightCarrier') ?? 'Flight carriers';
  }
  return ruleConditionsToTextMap.get(key as CommissionsConditionTypes) ?? key;
}

function formatCommissionRuleValue(rule: CommissionRuleValue): string {
  if (typeof rule !== 'object' || rule == null) {
    return String(rule);
  }
  const code = 'code' in rule ? rule.code : rule.airlineCode ?? '';
  const name = 'name' in rule ? rule.name : rule.airlineName ?? code;
  if (name && code && name !== code) {
    return `${name} (${code})`;
  }
  return code || name || '';
}

function getRuleInfo(key: string, rules: Array<CommissionRuleValue>) {
  switch (key) {
    case 'margin': {
      const [low, high] = rules;
      if (low === undefined || high === undefined) {
        return `${rules.length}`;
      }
      return `${formatCommissionRuleValue(low)}% - ${formatCommissionRuleValue(high)}%`;
    }
    case 'minTotalSpend':
      return rules[0] != null ? String(rules[0]) : '';
    default:
      return `${rules.length}`;
  }
}

function getRuleText(key: string, rules: Array<CommissionRuleValue>, mappedRules: MappedRules) {
  switch (key) {
    case 'agent': {
      const agentsMap = mappedRules.agents;
      if (!agentsMap) {
        return rules.map(formatCommissionRuleValue).join(' • ');
      }
      return rules
        .map((rule) => (typeof rule === 'string' ? agentsMap[rule] ?? rule : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    case 'agency': {
      const agenciesMap = mappedRules.agencies;
      if (!agenciesMap) {
        return rules.map(formatCommissionRuleValue).join(' • ');
      }
      return rules
        .map((rule) => (typeof rule === 'string' ? agenciesMap[rule] ?? rule : formatCommissionRuleValue(rule)))
        .join(' • ');
    }
    default:
      return rules.map(formatCommissionRuleValue).join(' • ');
  }
}

export function AgentHubCommissionRules({ rules, agents, agencies }: Props) {
  return (
    <Box display="flex" flexWrap="wrap" gap={1}>
      {Object.entries(rules).map(([key, rules]: [string, Array<CommissionRuleValue>]) => (
        <Tooltip key={key} title={getRuleText(key, rules, { agents, agencies })}>
          <Chip
            sx={{ backgroundColor: '#00000014' }}
            label={`${commissionRuleTypeLabel(key)} (${getRuleInfo(key, rules)})`}
            variant="filled"
          />
        </Tooltip>
      ))}
    </Box>
  );
}
